import { AttachmentResponseDto } from '@issuemanager/api-client';
import {
  ControlledFileUploader,
  FileList,
} from '@issuemanager/apps/client/components';
import { useAtlasSemanticThemeTokens } from '@issuemanager/apps/client/theme';
import { IssuePermissionsEnum, IssueStatusEnum } from '@issuemanager/constants';
import { Stack, Typography } from '@mui/material';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EmptyWorkflowState } from '../../components/empty-workflow-state';
import { GroupedDocumentList } from '../../components/grouped-document-list';
import { useGroupedAttachments } from '../../hooks/use-grouped-attachments';
import { useIssueStatusInfo } from '../../hooks/use-issue-status-info';
import { AssignIssueFormValues } from '../../hooks/use-manage-issue-form';
import { useIssueOutletContext } from '../../hooks/useIssueOutletContext';

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<AssignIssueFormValues, any, AssignIssueFormValues>;
  isEditing: boolean;
  attachments: AttachmentResponseDto[];
  isIssueEditable: boolean;
  onEdit: () => void;
};

export function Documents({
  form,
  isEditing,
  attachments,
  isIssueEditable,
  onEdit,
}: Props): React.JSX.Element {
  const semantic = useAtlasSemanticThemeTokens();
  const { t } = useTranslation('issue');
  const { issue, userRoles: issueUserRoles } = useIssueOutletContext();
  const { isNotYetAssigned } = useIssueStatusInfo({
    status: issue.data?.status,
    actionProgress: issue.data?.actionProgress,
    approvalStatus: issue.data?.approvalStatus,
  });

  const isInProgress = issue.data?.status === IssueStatusEnum.IN_PROGRESS;

  const canAddAttachments =
    (isNotYetAssigned && issueUserRoles.data?.isIssueTypeManager) ||
    (isInProgress && issueUserRoles.data?.isManager);

  const showFileUploader = isEditing && canAddAttachments;
  const showEmptyState =
    !attachments.length && isIssueEditable && canAddAttachments;

  const { regularAttachments, approvalAttachments } =
    useGroupedAttachments(attachments);

  return (
    <Stack data-testid='documents' spacing={4}>
      <Typography variant='h3' fontWeight='medium'>
        {t('form.sections.documents')}
      </Typography>

      {showFileUploader && (
        <>
          <ControlledFileUploader
            control={form.control}
            name='attachments'
            fullWidth={false}
            initialFileList={isInProgress ? [] : attachments}
            marginRight={semantic.spacing['2xl'].value}
            permissions={[
              IssuePermissionsEnum.IsIssueTypeManager,
              IssuePermissionsEnum.IsManager,
            ]}
          />
          {isInProgress && (
            <FileList files={attachments} showVerticalDividers={false} />
          )}
        </>
      )}

      {!showFileUploader && showEmptyState && (
        <EmptyWorkflowState
          onEdit={onEdit}
          description={t('manage.documents.empty.description')}
        />
      )}

      {!showFileUploader && !showEmptyState && (
        <GroupedDocumentList
          regularAttachments={regularAttachments}
          approvalAttachments={approvalAttachments}
        />
      )}
    </Stack>
  );
}
